/**
 * Lange Antworten zuklappen — in EINEM Schritt, ohne den Leser zu verschieben.
 * (01.10.2026, Ticket 2873)
 *
 * Martin, 29.07.: *„Vielleicht ist es gut, wenn man zu lange Textpassagen
 * einklappt, sodass man beim Scrollen nicht ewig an einem Text festhängt."*
 * Die Regel blieb: Was länger ist als etwa zwei Handy-Bildschirme, bekommt
 * einen Deckel und „Weiterlesen"; die zuletzt erzeugte Antwort bleibt offen,
 * die vorige klappt zu.
 *
 * ## Was daran den Verlauf springen ließ
 *
 * Das Zuklappen lief als `requestAnimationFrame` je Blase, EIN Bild nach dem
 * Einfügen, und niemand rechnete danach den Ort nach:
 *
 *   - Nachladen älterer Nachrichten: 80 Blasen kommen oben dazu, `scrollTop`
 *     wird auf den alten Ort gesetzt — und im nächsten Bild klappen die langen
 *     unter den Blasen zu, die der Leser sieht. Im Nachbau ohne Verankerung
 *     (WebKit) stand die Bezugsblase danach 7.066 Punkte woanders.
 *   - Neuaufbau des Verlaufs (Rückkehr nach laufendem Zug, „Verlauf neu holen"):
 *     erst die ganze Liste aufgeklappt (Chat 14.936 Punkte hoch), ein Bild
 *     später zugeklappt (11.804). In Martins Journal: `hoehe=39966>28805`,
 *     −11.161 Punkte in einem Ereignis.
 *   - Kaltstart: Der Chat ist versteckt, `scrollHeight` ist 0 — der Code merkte
 *     sich das („sonst würde keine Blase eingeklappt") und klappte dann gar
 *     nichts mehr zu. Derselbe Verlauf war so 14.936 oder 11.804 Punkte hoch,
 *     je nachdem, auf welchem Weg er gezeichnet wurde.
 *   - Die Antwort, die er gerade liest, klappte zu, sobald die nächste kam.
 *   - Beim Nachladen wurde jede ältere Blase zur „zuletzt offenen" — und die
 *     wirklich letzte Antwort klappte zu.
 *
 * ## Die Regel
 *
 * Eine Blase wird vorgemerkt und in EINEM Durchgang geprüft: erst alles lesen
 * (Höhe, Sichtfeld), dann alles schreiben, dann den Ort halten
 * (lib/scroll-ort.ts) — im selben Schritt, vor dem nächsten Bild.
 *
 *   - Was der Leser gerade sieht, bleibt offen und wird nach dem nächsten
 *     Scrollen (Ruhe von 400 ms) noch einmal geprüft.
 *   - Eine Blase, die gerade noch gefüllt wird (Antwort im Strom), klappt
 *     frühestens 1,5 s nach dem letzten Stück zu.
 *   - Ist der Verlauf versteckt, bleibt alles vorgemerkt; sobald er eine Größe
 *     hat (ResizeObserver), geht es los.
 *   - Bewegt der Leser den Verlauf (Finger, Nachlaufen, Ausfedern — lib/scroll-ruhe.ts),
 *     klappt nichts zu: Das Ort-Halten schreibt `scrollTop`, und das bricht am iPhone das
 *     Nachlaufen ab. Gemessen im echten WebKit (pre.3607, Fall d): eine Antwort von einem
 *     anderen Gerät, die vorige lange klappt über ihm zu — ein Schreiben mitten im Wisch.
 *     Jetzt wartet der Durchgang, bis es still ist.
 *   - Welche Antwort offen bleibt (die zuletzt erzeugte) und welche zuklappt,
 *     entscheidet `gefuellt(bubble, art)` — „neu“, „strom“, „verlauf“ — an EINER
 *     Stelle (vorher: eine Variable in app.ts, deren Verhalten nur als
 *     Quelltext-Muster geprüft werden konnte).
 *
 * ## Eine Blase, die der Leser nie offen gesehen hat (02.10.2026, pre.3620)
 *
 * Journal 21:32:53, Rückkehr in die App: der Verlauf wurde neu aufgebaut (Zug lief beim Wegschieben),
 * `hoehe=17806>19460` und das Nachziehen ans Ende (+1.666), 0,4 s später fünf Blasen zu (`−1.654`,
 * `@class(+is-collapsible,+is-collapsed)…×5`). Fünf Blasen erschienen erst AUFGEKLAPPT und klappten nach der
 * Lesepause zu: Die Regeln „was er sieht, bleibt offen“ und „bewegt sich etwas, warte“ galten auch für Blasen,
 * die er noch nie gesehen hatte — der Neuaufbau löst selbst Scroll-Ereignisse aus (Ende des Verlaufs rückt).
 * Nachbau (tests/browser/scroll-iphone-webkit.mts, Fall h2: Neuaufbau, der Daumen liegt dabei auf): auf
 * pre.3608 zehn lange Blasen offen gezeichnet und danach zugeklappt, Höhe je Bild +2.898/−2.608.
 * Jetzt: Eine Blase ist FRISCH von ihrer Füllung (`gefuellt`, jede Art) bis zum ersten Bild, in dem der Verlauf
 * eine Größe hatte — der Leser hat sie nie gesehen. Eine frische lange Blase klappt im ersten Durchgang zu —
 * auch im Sichtfeld, auch während Bewegung (dann ohne Ort-Halten: sie ist Teil des Einfügens, an ihr hatte der
 * Leser keinen Ort). Der erste Durchgang läuft vor dem Bild (synchron im Neuaufbau, im `requestAnimationFrame`
 * des Vormerkens oder im ResizeObserver) — gezeichnet wird sie nur noch zugeklappt. Eine schon gezeichnete
 * Blase, die neu gefüllt wird (die ganze Fassung einer abgerissenen Antwort), ist nicht frisch: er kennt sie.
 * Ausnahme: die jüngste Antwort im Verlauf (keine Antwort-Blase mehr nach ihr) bleibt offen, wie bisher.
 *
 * ⚠️ Das Zuklappen ist ein Vorschlag des Hauses, kein Befehl an den Leser: Ein
 *    Klick auf „Weiterlesen" bleibt so lange offen, bis er „Einklappen" tippt
 *    (`is-collapsible` wird nie wieder angefasst).
 */
import { haltenOhneSprung } from "./scroll-ort.js";
import { gesteuert } from "./ruckel-schalter.js";
import { nutzerScrolltGerade, RUHE_MS } from "./scroll-ruhe.js";

/** Höher als das (Punkte), und der Inhalt bekommt einen Deckel — rund zwei Handy-Bildschirme. */
export const BUBBLE_MAX_HOEHE = 560;
/**
 * 19.10.2026 (Martin: lange Texte nicht als Wand): Ab so vielen Zeichen gilt eine Antwort als lang und klappt zu, auch wenn
 * sie (breiter Schirm) unter `BUBBLE_MAX_HOEHE` bleibt. Darunter hängt die Vorlese-Karte (vorlese-karte.ts, LANGER_TEXT_ZEICHEN).
 */
export const KLAPPEN_AB_ZEICHEN = 1500;
/** So lange nach dem letzten Stück gilt eine Antwort als „noch im Entstehen". */
export const RUHE_NACH_STROM_MS = 1500;
/** So lange muss der Verlauf still stehen, bevor eine im Sichtfeld offen gelassene Blase erneut geprüft wird. */
export const LESEPAUSE_MS = 400;

/** Klassen der Blase — style.css (`.bubble.is-collapsible.is-collapsed > .bubble-content`). */
export const KLASSE_KLAPPBAR = "is-collapsible";
export const KLASSE_ZU = "is-collapsed";

/** Reine Entscheidung: ist der Inhalt hoch genug für einen Deckel? */
export function sollKlappen(inhaltHoehe: number, max: number = BUBBLE_MAX_HOEHE): boolean {
  return inhaltHoehe > max;
}

/** Reine Entscheidung: ist der Text so lang, dass er zuklappt — unabhängig von der Höhe? */
export function sollKlappenText(zeichen: number, ab: number = KLAPPEN_AB_ZEICHEN): boolean {
  return zeichen >= ab;
}

/** Der Knopf „Weiterlesen" / „Einklappen" unter dem Inhalt. */
export function haengeMehrKnopf(bubble: HTMLElement): HTMLButtonElement {
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = "bubble-mehr";
  const setzeText = (): void => {
    const zu = bubble.classList.contains(KLASSE_ZU);
    knopf.textContent = zu ? "Weiterlesen" : "Einklappen";
    knopf.setAttribute("aria-expanded", zu ? "false" : "true");
  };
  setzeText();
  knopf.addEventListener("click", (ev) => {
    ev.stopPropagation();
    const warZu = bubble.classList.contains(KLASSE_ZU);
    bubble.classList.toggle(KLASSE_ZU);
    setzeText();
    // Beim Zuklappen an den Anfang der Blase springen — sonst steht man
    // plötzlich mitten im Nichts, weil der Text unter einem verschwindet.
    if (!warZu) bubble.scrollIntoView({ block: "nearest" });
  });
  bubble.appendChild(knopf);
  return knopf;
}

/**
 * Nach dem Neuaufbau einer Blase (fillBotBubble leert sie ganz) den Knopf
 * wieder hinhängen: Die Klassen bleiben stehen, der Knopf nicht — ohne ihn
 * stünde der Deckel da und niemand käme zum Rest.
 */
export function stelleMehrKnopfHer(bubble: HTMLElement): void {
  if (!bubble.classList.contains(KLASSE_KLAPPBAR)) return;
  if (bubble.querySelector(":scope > .bubble-mehr")) return;
  haengeMehrKnopf(bubble);
}

function klappeZu(bubble: HTMLElement): void {
  bubble.classList.add(KLASSE_KLAPPBAR, KLASSE_ZU);
  haengeMehrKnopf(bubble);
}

/** Wovon der Klapper abhängt — im Test ersetzbar (jsdom kennt kein Layout). */
export interface KlapperUmgebung {
  jetzt: () => number;
  /** Höhe des Inhalts in Punkten, auch wenn er schon gedeckelt wäre. */
  messeInhalt: (inhalt: HTMLElement) => number;
  /** Wie viele Zeichen Text hat der Inhalt? */
  zeichenInhalt: (inhalt: HTMLElement) => number;
  /** Sieht der Leser (einen Teil von) dieser Blase? */
  imSichtfeld: (bubble: HTMLElement, chat: HTMLElement) => boolean;
  /** Hat der Verlauf eine Größe? */
  hatGroesse: (chat: HTMLElement) => boolean;
  /** Eine Änderung ausführen und den Ort halten. */
  halte: (chat: HTMLElement, aenderung: () => void) => number;
  /** Bewegt der Leser den Verlauf gerade? Dann wird nichts geschrieben. */
  bewegtSich: (chat: HTMLElement) => boolean;
  /** Steht nach dieser Blase keine Antwort-Blase mehr (die jüngste Antwort im Verlauf)? */
  istJuengste: (bubble: HTMLElement) => boolean;
  setzeZeit: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  loescheZeit: (id: ReturnType<typeof setTimeout>) => void;
  naechstesBild: (fn: () => void) => void;
}

const STANDARD: KlapperUmgebung = {
  jetzt: () => performance.now(),
  messeInhalt: (inhalt) => inhalt.scrollHeight,
  zeichenInhalt: (inhalt) => (inhalt.textContent ?? "").length,
  imSichtfeld: (bubble, chat) => {
    const r = bubble.getBoundingClientRect();
    const k = chat.getBoundingClientRect();
    return r.bottom > k.top && r.top < k.bottom;
  },
  hatGroesse: (chat) => chat.clientHeight > 0,
  halte: haltenOhneSprung,
  bewegtSich: (chat) => nutzerScrolltGerade(chat),
  istJuengste: (bubble) => {
    for (let n = bubble.nextElementSibling; n; n = n.nextElementSibling) if (n.matches(".bubble.bot")) return false;
    return true;
  },
  setzeZeit: (fn, ms) => setTimeout(fn, ms),
  loescheZeit: (id) => clearTimeout(id),
  naechstesBild: (fn) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  },
};

/**
 * Woher die Füllung einer Antwort-Blase kommt:
 *   "neu"     eine Antwort, wie sie jetzt entsteht — bleibt offen, die vorige klappt zu.
 *   "strom"   ein Stück einer Antwort, die noch wächst — wie „neu", und sie klappt
 *             frühestens 1,5 s nach dem letzten Stück zu.
 *   "verlauf" eine Blase aus dem Verlauf (nachgeladen, auf die ganze Fassung gehoben) —
 *             sie ist Vergangenheit und klappt selbst zu; die letzte Antwort bleibt, wie sie ist.
 */
export type FuellArt = "neu" | "strom" | "verlauf";

export interface Klapper {
  /** Diese Blase soll zugeklappt werden, sobald sie lang genug und außer Sicht ist. */
  vormerken(bubble: HTMLElement): void;
  /**
   * Eine Antwort-Blase ist (neu) gefüllt: Welche bleibt offen, welche klappt zu?
   * „neu“ und „strom“: diese Blase wird die offene, die vorige offene wird vorgemerkt.
   * „verlauf“: diese Blase wird vorgemerkt — außer sie IST die offene (die ganze Fassung
   * einer eben gestreamten Antwort), dann bleibt alles, wie es ist.
   */
  gefuellt(bubble: HTMLElement, art: FuellArt): void;
  /** Die zuletzt offen gelassene Antwort (oder `null`). */
  offeneAntwort(): HTMLElement | null;
  /** Diese Blase wird gerade (weiter) gefüllt — frühestens `RUHE_NACH_STROM_MS` später zuklappen. */
  strom(bubble: HTMLElement): void;
  /** Jetzt prüfen und zuklappen (im selben Schritt, Ort gehalten). Gibt zurück, wie viele zugeklappt wurden. */
  leeren(): number;
  /** Wie viele Blasen noch vorgemerkt sind. */
  offen(): number;
}

/** Den Klapper für einen Verlauf bauen und an dessen Größe und Scrollen hängen. */
export function erzeugeKlapper(chat: HTMLElement, umgebung: Partial<KlapperUmgebung> = {}): Klapper {
  const u: KlapperUmgebung = { ...STANDARD, ...umgebung };
  const ausstehend = new Set<HTMLElement>();
  const imStrom = new WeakMap<HTMLElement, number>();
  /** Gefüllt und noch nie gezeichnet — der Leser hat sie nie gesehen (s. Kopf). */
  const frisch = new Set<HTMLElement>();
  /** Einmal gezeichnet (mit Größe des Verlaufs): nie wieder frisch, auch nach neuer Füllung. */
  const gezeichnet = new WeakSet<HTMLElement>();
  let frischEndeGeplant = false;
  /**
   * Zwei Bilder weiter ist das erste Bild nach der Füllung gemalt: Hatte der Verlauf dabei eine Größe, hat der
   * Leser die Blasen gesehen — sie sind nicht mehr frisch. Ohne Größe (versteckt) bleiben sie es; der nächste
   * Durchgang mit Größe plant wieder.
   */
  const planeFrischEnde = (): void => {
    if (frischEndeGeplant || frisch.size === 0) return;
    frischEndeGeplant = true;
    u.naechstesBild(() =>
      u.naechstesBild(() => {
        frischEndeGeplant = false;
        for (const b of frisch) if (!b.isConnected) frisch.delete(b);
        if (!u.hatGroesse(chat)) return;
        for (const b of frisch) gezeichnet.add(b);
        frisch.clear();
      }),
    );
  };
  let zeitgeber: ReturnType<typeof setTimeout> | null = null;
  let bildGeplant = false;
  /** Die zuletzt offen gelassene Antwort — wird beim nächsten Zug eingeklappt. */
  let zuletztOffen: HTMLElement | null = null;

  const planen = (ms: number): void => {
    if (zeitgeber !== null) u.loescheZeit(zeitgeber);
    zeitgeber = u.setzeZeit(() => {
      zeitgeber = null;
      leeren();
    }, ms);
  };

  const leeren = (): number => {
    // Nichts vorgemerkt: frische Blasen können trotzdem warten, als gesehen zu gelten (Prüfbefund pre.3620).
    if (ausstehend.size === 0) {
      planeFrischEnde();
      return 0;
    }
    // Versteckt (Kaltstart, andere Ansicht): nichts messen, alles behalten.
    if (!u.hatGroesse(chat)) return 0;
    // Er bewegt den Verlauf: nichts anfassen, gleich wieder nachsehen — außer frischen Blasen (s. Kopf).
    const bewegt = u.bewegtSich(chat);
    const jetzt = u.jetzt();
    const zuKlappen: HTMLElement[] = [];
    let naechsterVersuch: number | null = null;
    let wartetAufRuhe = false;

    // 1. Nur lesen.
    for (const bubble of Array.from(ausstehend)) {
      if (!bubble.isConnected || bubble.classList.contains(KLASSE_KLAPPBAR)) {
        ausstehend.delete(bubble);
        continue;
      }
      const neu = frisch.has(bubble) && !u.istJuengste(bubble);
      if (bewegt && !neu) {
        wartetAufRuhe = true;
        continue;
      }
      const inhalt = bubble.querySelector<HTMLElement>(":scope > .bubble-content");
      if (!inhalt) {
        ausstehend.delete(bubble);
        continue;
      }
      const ruhigAb = (imStrom.get(bubble) ?? -Infinity) + RUHE_NACH_STROM_MS;
      if (jetzt < ruhigAb) {
        naechsterVersuch = Math.min(naechsterVersuch ?? Infinity, ruhigAb - jetzt + 20);
        continue;
      }
      if (!sollKlappen(u.messeInhalt(inhalt)) && !sollKlappenText(u.zeichenInhalt(inhalt))) {
        ausstehend.delete(bubble);
        continue;
      }
      // Er liest sie gerade: offen lassen, nach dem nächsten Scrollen noch einmal prüfen. Eine frische hat er nie gesehen.
      if (!neu && u.imSichtfeld(bubble, chat)) continue;
      zuKlappen.push(bubble);
      ausstehend.delete(bubble);
    }

    // 2. Schreiben — in einem Zug, mit gehaltenem Ort.
    if (zuKlappen.length > 0) {
      const klappe = (): void => {
        for (const bubble of zuKlappen) klappeZu(bubble);
      };
      // In Bewegung sind es nur frische: kein Ort-Halten (es schriebe `scrollTop` mitten in den Wisch).
      if (bewegt) klappe();
      else u.halte(chat, klappe);
    }
    planeFrischEnde();
    if (wartetAufRuhe) naechsterVersuch = Math.min(naechsterVersuch ?? Infinity, RUHE_MS);
    if (naechsterVersuch !== null) planen(naechsterVersuch);
    return zuKlappen.length;
  };

  const vormerken = (bubble: HTMLElement): void => {
    ausstehend.add(bubble);
    if (bildGeplant) return;
    bildGeplant = true;
    u.naechstesBild(() => {
      bildGeplant = false;
      leeren();
    });
  };

  // Nach dem Scrollen noch einmal schauen, was im Sichtfeld offen geblieben ist.
  chat.addEventListener(
    "scroll",
    gesteuert("wachen", () => {
      if (ausstehend.size > 0) planen(LESEPAUSE_MS);
    }),
    { passive: true },
  );
  // Der Verlauf bekommt eine Größe (Ansicht wird sichtbar): jetzt kann gemessen werden.
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(
      gesteuert(
        "wachen",
        () => {
          if (ausstehend.size > 0) leeren();
          /*
           * Prüfbefund zu pre.3620: Kam die erste lange Antwort, während der Chat versteckt war, sah das
           * Frisch-Ende nach zwei Bildern keine Größe und plante nie wieder — die Blase blieb „frisch“, und
           * bei der nächsten Antwort klappte sie zu, während er sie las. Bekommt der Verlauf eine Größe,
           * wird jetzt gezeichnet: das Frisch-Ende neu planen.
           */
          planeFrischEnde();
        },
        true,
      ),
    ).observe(chat);
  }

  const strom = (bubble: HTMLElement): void => {
    imStrom.set(bubble, u.jetzt());
  };

  return {
    vormerken,
    strom,
    gefuellt: (bubble, art) => {
      if (!gezeichnet.has(bubble)) {
        frisch.add(bubble);
        planeFrischEnde();
      }
      if (art === "strom") strom(bubble);
      if (art === "verlauf") {
        // Eine nachgeladene Blase ist nicht „die letzte Antwort": Sie darf weder die
        // wirkliche letzte Antwort zuklappen lassen noch deren Platz einnehmen.
        if (bubble !== zuletztOffen) vormerken(bubble);
        return;
      }
      if (zuletztOffen && zuletztOffen !== bubble) vormerken(zuletztOffen);
      zuletztOffen = bubble;
    },
    offeneAntwort: () => zuletztOffen,
    leeren,
    offen: () => ausstehend.size,
  };
}
